/**
 * Environment Configuration
 * Controls various settings for development and production
 */

/**
 * Get the application basename (public URL path)
 * Priority:
 * 1. PUBLIC_URL environment variable (set during build)
 * 2. Extract from apiBaseUrl if it contains a path
 * 3. Fallback to default based on NODE_ENV
 */
const getBasename = (): string => {
  let basename: string;
  let source: string;
  
  // Check PUBLIC_URL first (set by React build process)
  if (process.env.PUBLIC_URL) {
    basename = process.env.PUBLIC_URL;
    source = 'PUBLIC_URL env var';
    console.log('🔍 DEBUG getBasename - Using PUBLIC_URL:', {
      'raw value': JSON.stringify(process.env.PUBLIC_URL),
      'length': process.env.PUBLIC_URL?.length,
      'charCodes': Array.from(process.env.PUBLIC_URL || '').map(c => c.charCodeAt(0)),
      'hasSpaces': process.env.PUBLIC_URL?.includes(' ')
    });
  } else {
    // Extract from apiBaseUrl if it contains a path segment
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 
      (process.env.NODE_ENV === 'production' 
        ? 'http://10.99.115.45:8080/rhprojects/auditcare/backend/api' 
        : 'http://localhost/rh-chd/AuditCare/backend/api');
    
    // Try to extract path from API URL
    try {
      const url = new URL(apiBaseUrl);
      const pathParts = url.pathname.split('/').filter(p => p);
      // Remove 'backend' and 'api' from the path to get the app basename
      const appPathParts = pathParts.filter(p => p !== 'backend' && p !== 'api');
      if (appPathParts.length > 0) {
        basename = '/' + appPathParts.join('/');
        source = 'extracted from apiBaseUrl';
        console.log('🔍 DEBUG getBasename - Extracted from apiBaseUrl:', {
          'apiBaseUrl': apiBaseUrl,
          'pathParts': pathParts,
          'appPathParts': appPathParts,
          'basename': JSON.stringify(basename)
        });
      } else {
        // Fallback based on environment
        basename = process.env.NODE_ENV === 'production' 
          ? '/rhprojects/auditcare' 
          : '/auditcare';
        source = 'fallback (NODE_ENV)';
      }
    } catch (e) {
      // If URL parsing fails, use fallback
      basename = process.env.NODE_ENV === 'production' 
        ? '/rhprojects/auditcare' 
        : '/auditcare';
      source = 'fallback (URL parse error)';
      console.log('🔍 DEBUG getBasename - URL parse error, using fallback:', e);
    }
  }
  
  // DEBUG: Log before trim
  console.log('🔍 DEBUG getBasename - Before trim:', {
    'source': source,
    'basename': JSON.stringify(basename),
    'length': basename.length,
    'charCodes': Array.from(basename).map(c => c.charCodeAt(0)),
    'hasSpaces': basename.includes(' '),
    'startsWithSpace': basename.startsWith(' '),
    'endsWithSpace': basename.endsWith(' ')
  });
  
  // Trim any leading/trailing whitespace and ensure it starts with /
  const beforeTrim = basename;
  basename = basename.trim();
  const trimmed = beforeTrim !== basename;
  
  if (!basename.startsWith('/')) {
    basename = '/' + basename;
  }
  
  // Debug: Log the final basename
  console.log('📍 Basename calculated:', {
    'source': source,
    'final basename': JSON.stringify(basename),
    'length': basename.length,
    'charCodes': Array.from(basename).map(c => c.charCodeAt(0)),
    'hasSpaces': basename.includes(' '),
    'wasTrimmed': trimmed,
    'beforeTrim': JSON.stringify(beforeTrim),
    'afterTrim': JSON.stringify(basename)
  });
  
  return basename;
};

export const config = {
  // Mock Data Configuration
  useMockData: process.env.REACT_APP_USE_MOCK_DATA === 'true',
  
  // API Configuration
  apiBaseUrl: process.env.REACT_APP_API_BASE_URL || (process.env.NODE_ENV === 'production' 
    ? 'http://10.99.115.45:8080/rhprojects/auditcare/backend/api' 
    : 'http://localhost/rh-chd/AuditCare/backend/api'),
  
  // Router Basename (derived from PUBLIC_URL or apiBaseUrl)
  basename: getBasename(),
  
  // Development Features
  debugMode: process.env.NODE_ENV === 'development' || process.env.REACT_APP_DEBUG_MODE === 'true',
  logLevel: process.env.REACT_APP_LOG_LEVEL || 'info',
  
  // Application Settings
  appName: 'Mortality Audit System',
  version: '1.0.0',
  
  // Mock Data Settings
  mockData: {
    // Simulate network delays (in milliseconds)
    apiDelay: {
      min: 200,
      max: 800,
      default: 500
    },
    
    // Mock data refresh intervals
    refreshInterval: 30000, // 30 seconds
    
    // Enable/disable specific mock features
    features: {
      notifications: true,
      auditLogs: true,
      realTimeUpdates: false
    }
  }
};

// Development helper - Always show configuration in development
console.log('🔧 Environment Configuration:', {
  useMockData: config.useMockData,
  debugMode: config.debugMode,
  apiBaseUrl: config.apiBaseUrl,
  basename: config.basename,
  logLevel: config.logLevel,
  NODE_ENV: process.env.NODE_ENV,
  PUBLIC_URL: process.env.PUBLIC_URL,
  REACT_APP_USE_MOCK_DATA: process.env.REACT_APP_USE_MOCK_DATA,
  REACT_APP_API_BASE_URL: process.env.REACT_APP_API_BASE_URL
});

export default config;
